import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

export default function JobsSummaryStrip({
  metrics = [],
  timelineBuckets = [],
  onSelectDay,
  onPreviousWeek,
  onNextWeek,
  onToday,
  onSelectCalendarDay,
  selectedDateKey = "",
  weekLabel = "",
  weekRangeLabel = "",
  timelineWeekOffset = 0,
  timelineLoading = false,
  activeTechnicians = [],
  onOpenSettings,
}) {
  const [slideDirection, setSlideDirection] = useState(null);

  const handleWeekShift = (direction) => {
    setSlideDirection(direction < 0 ? "previous" : "next");

    if (direction < 0) {
      onPreviousWeek?.();
      return;
    }

    onNextWeek?.();
  };

  const handleTodayClick = () => {
    setSlideDirection(timelineWeekOffset > 0 ? "previous" : "next");
    onToday?.();
  };

  const handleCalendarChange = (event) => {
    if (!event.target.value) {
      return;
    }

    setSlideDirection(
      event.target.value > selectedDateKey ? "next" : "previous",
    );
    onSelectCalendarDay?.(event.target.value);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "rgba(255,255,255,0.18)",
        background:
          "linear-gradient(130deg, #0b3242 0%, #10516a 48%, #0e8cc0 100%)",
        color: "#f6fbfc",
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        sx={{ justifyContent: "space-evenly", alignItems: { lg: "center" } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              mt: 0.2,
              alignItems: "center",
              justifyContent: "space-between",
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={0.6}
                sx={{ alignItems: "center" }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {weekLabel}
                </Typography>
                {timelineLoading && (
                  <CircularProgress size={12} sx={{ color: "inherit" }} />
                )}
              </Stack>
              <Typography
                variant="caption"
                sx={{ display: "block", lineHeight: 1, opacity: 0.82 }}
              >
                {weekRangeLabel}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleTodayClick}
                disabled={timelineWeekOffset === 0}
                sx={{
                  color: "inherit",
                  borderColor: "rgba(255,255,255,0.28)",
                  minHeight: 28,
                  px: 1.2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&.Mui-disabled": {
                    color: "rgba(255,255,255,0.42)",
                    borderColor: "rgba(255,255,255,0.14)",
                  },
                }}
              >
                Today
              </Button>
              <Tooltip title="Select day">
                <Box
                  sx={{
                    position: "relative",
                    width: 32,
                    height: 28,
                    flex: "0 0 32px",
                    color: "inherit",
                    display: "inline-flex",
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="Select day"
                    tabIndex={-1}
                    sx={{
                      width: "100%",
                      height: "100%",
                      color: "inherit",
                      border: "1px solid rgba(255,255,255,0.28)",
                      borderRadius: 1,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.10)",
                      },
                    }}
                  >
                    <CalendarMonthOutlinedIcon fontSize="small" />
                  </IconButton>
                  <Box
                    component="input"
                    type="date"
                    value={selectedDateKey}
                    onChange={handleCalendarChange}
                    aria-label="Select timeline day"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                    }}
                  />
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.8, minWidth: 0 }}>
            <Tooltip title="Previous week">
              <IconButton
                size="small"
                aria-label="Show previous week"
                onClick={() => handleWeekShift(-1)}
                sx={{
                  alignSelf: "stretch",
                  width: 32,
                  flex: "0 0 32px",
                  color: "inherit",
                  borderRadius: 2,
                  backgroundColor: "rgba(255,255,255,0.12)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                }}
              >
                <ChevronLeftOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Stack
              key={`${weekLabel}-${weekRangeLabel}`}
              direction="row"
              spacing={0.9}
              useFlexGap
              sx={{
                minWidth: 0,
                maxWidth: "100%",
                overflowX: "auto",
                overflowY: "hidden",
                pb: 0.4,
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255,255,255,0.38) transparent",
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255,255,255,0.38)",
                  borderRadius: 8,
                },
                animation: slideDirection
                  ? `${slideDirection === "next" ? "timelineSlideFromRight" : "timelineSlideFromLeft"} 180ms ease-out`
                  : "none",
                "@keyframes timelineSlideFromRight": {
                  "0%": { opacity: 0.45, transform: "translateX(34px)" },
                  "100%": { opacity: 1, transform: "translateX(0)" },
                },
                "@keyframes timelineSlideFromLeft": {
                  "0%": { opacity: 0.45, transform: "translateX(-34px)" },
                  "100%": { opacity: 1, transform: "translateX(0)" },
                },
              }}
            >
              {timelineBuckets.map((bucket) => (
                <Box
                  key={bucket.dateKey || bucket.label}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectDay?.(bucket.dateKey)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectDay?.(bucket.dateKey);
                    }
                  }}
                  sx={{
                    flex: "0 0 84px",
                    minWidth: bucket.selected ? 84 : 40,
                    px: 1,
                    py: 0.8,
                    borderRadius: 2,
                    cursor: "pointer",
                    backgroundColor: bucket.selected
                      ? "rgba(121, 242, 199, 0.24)"
                      : "rgba(255,255,255,0.10)",
                    border: "1px solid",
                    borderColor: bucket.selected
                      ? "rgba(121, 242, 199, 0.65)"
                      : "rgba(255,255,255,0.20)",
                    "&:hover": {
                      backgroundColor: bucket.selected
                        ? "rgba(121, 242, 199, 0.28)"
                        : "rgba(255,255,255,0.16)",
                    },
                    "&:focus-visible": {
                      outline: "2px solid rgba(121, 242, 199, 0.72)",
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.88,
                      textAlign: "center",
                      display: "block",
                    }}
                  >
                    {bucket.label}
                  </Typography>
                  <Tooltip title="Routes for this day">
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 800,
                        lineHeight: 1.1,
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.35,
                        "& .MuiSvgIcon-root": {
                          fontSize: 14,
                        },
                      }}
                    >
                      <RouteOutlinedIcon /> {bucket.routeCount || 0}
                    </Typography>
                  </Tooltip>
                  <Tooltip title="Jobs for this day">
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 800,
                        lineHeight: 1.1,
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.35,
                        "& .MuiSvgIcon-root": {
                          fontSize: 14,
                        },
                      }}
                    >
                      <AssignmentOutlinedIcon /> {bucket.count}
                    </Typography>
                  </Tooltip>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      mt: 0.3,
                      paddingTop: 0.5,
                      borderTop: "1px solid rgba(255,255,255,0.18)",
                      alignContent: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                        lineHeight: 1.1,
                        opacity: 0.82,
                        "& .MuiSvgIcon-root": {
                          fontSize: 12,
                        },
                      }}
                    >
                      <CheckCircleOutlinedIcon /> {bucket.completedCount || 0}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                        lineHeight: 1.1,
                        opacity: 0.82,
                        "& .MuiSvgIcon-root": {
                          fontSize: 12,
                        },
                      }}
                    >
                      <PriorityHighRoundedIcon /> {bucket.highUrgentCount || 0}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Tooltip title="Next week">
              <IconButton
                size="small"
                aria-label="Show next week"
                onClick={() => handleWeekShift(1)}
                sx={{
                  alignSelf: "stretch",
                  width: 32,
                  flex: "0 0 32px",
                  color: "inherit",
                  borderRadius: 2,
                  backgroundColor: "rgba(255,255,255,0.12)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                }}
              >
                <ChevronRightOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        <Stack
          direction="column"
          spacing={0.75}
          border={2}
          borderColor="rgba(255,255,255,0.18)"
          borderRadius={2}
          width={{ xs: "100%", sm: "420px" }}
          minWidth={{ md: "420px" }}
          height={{ xs: "150px", sm: "120px" }}
          sx={{ px: 1.5, py: 1, overflow: "hidden" }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <Typography
              variant="subtitle2"
              color="#f6fbfc"
              sx={{
                fontWeight: 800,
                mr: "auto",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Today's Stats
            </Typography>
            {metrics.map((metric) => {
              const metricLabel = String(metric.label || "").toLowerCase();
              const isVisible = metricLabel === "visible";
              const isOpen = metricLabel === "open";
              const isCompleted = metricLabel === "completed";
              return isVisible || isOpen || isCompleted ? (
                <Tooltip key={metric.label} title={metric.label}>
                  <Chip
                    size="small"
                    label={
                      isVisible ? (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            lineHeight: 1,
                          }}
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                          {metric.value}
                        </Box>
                      ) : (
                        `${metric.label}: ${metric.value}`
                      )
                    }
                    sx={{
                      height: 24,
                      backgroundColor: metric.highlight
                        ? "rgba(121, 242, 199, 0.18)"
                        : "rgba(255,255,255,0.12)",
                      color: "inherit",
                      borderRadius: 2,
                      fontWeight: 700,
                      "& .MuiChip-label": {
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1,
                      },
                    }}
                  />
                </Tooltip>
              ) : null;
            })}
          </Stack>
          <Stack
            spacing={0.25}
            sx={{
              minHeight: 0,
              overflowY: "auto",
              pr: 0.5,
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(121, 242, 199, 0.72) rgba(255,255,255,0.1)",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: 3,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(121, 242, 199, 0.72)",
                borderRadius: 3,
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "rgba(121, 242, 199, 0.9)",
              },
            }}
          >
            {activeTechnicians.length > 0 ? (
              activeTechnicians.map((technician) => (
                <Stack
                  key={technician.id}
                  direction="row"
                  spacing={0.75}
                  sx={{ alignItems: "center", py: 0.4 }}
                >
                  <PersonOutlineOutlinedIcon
                    sx={{ fontSize: 16, flexShrink: 0 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, flexShrink: 0, width: 82 }}
                    noWrap
                    title={technician.name}
                  >
                    {technician.name}
                  </Typography>
                  <RoomOutlinedIcon
                    sx={{ fontSize: 14, opacity: 0.85, flexShrink: 0 }}
                  />
                  <Typography
                    variant="caption"
                    noWrap
                    title={technician.roomName}
                    sx={{ opacity: 0.85, width: 72, flexShrink: 0 }}
                  >
                    {technician.roomName}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={technician.progressPercent}
                    aria-label={`${technician.name} assignment progress`}
                    aria-valuetext={`${technician.completedJobs} of ${technician.totalJobs} jobs completed`}
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "rgba(255,255,255,0.18)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "rgba(121, 242, 199, 0.85)",
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.82,
                      flexShrink: 0,
                      minWidth: 42,
                      textAlign: "right",
                    }}
                  >
                    {technician.completedJobs} / {technician.totalJobs}
                  </Typography>
                </Stack>
              ))
            ) : (
              <Typography
                variant="h1"
                sx={{
                  opacity: 0.7,
                  textAlign: "center",
                  py: 2,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                No technicians assigned today
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ alignItems: { md: "center" } }}
        >
          {onOpenSettings ? (
            <Tooltip title="Route template settings">
              <IconButton
                onClick={onOpenSettings}
                sx={{
                  color: "inherit",
                  border: "1px solid rgba(255,255,255,0.24)",
                  borderRadius: 2,
                }}
              >
                <RouteOutlinedIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
